import type { stacks } from "@dropbox/api-v2-client";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Cancel_UpdateStackEmoji } from "@mirage/analytics/events/types/cancel_update_stack_emoji";
import { PAP_Initiate_UpdateStackEmoji } from "@mirage/analytics/events/types/initiate_update_stack_emoji";
import { DashStackIcon } from "@mirage/mosaics/DCLWrappers/DashStackIcon";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { convertFeatureValueToBool } from "@mirage/service-experimentation/util";
import { EMOJI_CLEARED_SENTINEL_VALUE } from "@mirage/stacks/Helpers/Utils";
import { getDefaultColorIndex } from "@mirage/stacks/themes";
import {
  getStackIconUrl,
  getStackPapPropsWithFeatureLine,
} from "@mirage/stacks/utils";
import i18n from "@mirage/translations";
import { useRef, useState } from "react";
import { useUpdateStackEmoji, useUpdateStackLogo } from "../hooks";
import type { EmojiData } from "./EmojiPicker";
import { EmojiPicker } from "./EmojiPicker";
import styles from "./StackIconEditor.module.css";

export type StackIconEditorProps = {
  canEdit: boolean;
  mutationRequestId?: string;
  sessionId?: string;
  stack: stacks.Stack | null;
};

export const StackIconEditor = ({
  canEdit,
  mutationRequestId,
  sessionId,
  stack,
}: StackIconEditorProps) => {
  const { reportPapEvent } = useMirageAnalyticsContext();
  const [emojiPickerVisible, setEmojiPickerVisible] = useState(false);
  const updateStackEmoji = useUpdateStackEmoji(
    stack,
    mutationRequestId,
    sessionId,
  );
  const logoInputRef = useRef<HTMLInputElement>(null);
  const isStackLogoEnabled = convertFeatureValueToBool(
    useFeatureFlagValue("dash_2025_06_24_fss_stacks"),
  );

  const isNewStack = stack === null;
  const colorIndex = isNewStack
    ? getDefaultColorIndex()
    : stack?.stack_data?.color_index || getDefaultColorIndex();
  const customColor = stack?.stack_data?.custom_color;
  const emoji = stack?.stack_data?.emoji;

  const handleIconClick = () => {
    if (!canEdit || emojiPickerVisible) return;
    setEmojiPickerVisible(true);
    const papProps = getStackPapPropsWithFeatureLine(stack);
    reportPapEvent(PAP_Initiate_UpdateStackEmoji(papProps));
  };

  const handleEmojiSelect = (emoji: EmojiData) => {
    updateStackEmoji(emoji.native);
    setEmojiPickerVisible(false);
  };

  const handleEmojiSelectCancel = () => {
    setEmojiPickerVisible(false);
    const papProps = getStackPapPropsWithFeatureLine(stack);
    reportPapEvent(PAP_Cancel_UpdateStackEmoji(papProps));
  };

  const updateStackLogo = useUpdateStackLogo(stack);
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // Clear the input value to allow re-uploading the same file
    if (file && stack?.namespace_id) {
      await updateStackLogo(file);
    }
  };

  return (
    <div className={styles.stackIconContainer} data-testid="stackIcon">
      <DashStackIcon
        className={styles.stackIcon}
        colorIndex={colorIndex}
        aria-label={i18n.t("aria_emoji_button")}
        emoji={emoji || EMOJI_CLEARED_SENTINEL_VALUE}
        size="xlarge"
        onClick={handleIconClick}
        customColor={customColor}
        iconUrl={getStackIconUrl(stack)}
      />
      {isStackLogoEnabled && (
        <input
          type="file"
          accept="image/*"
          ref={logoInputRef}
          onChangeCapture={handleFileUpload}
          hidden
        />
      )}
      {emojiPickerVisible && (
        <EmojiPicker
          onCancel={handleEmojiSelectCancel}
          onSelect={handleEmojiSelect}
          onUpload={
            isStackLogoEnabled ? () => logoInputRef.current?.click() : undefined
          }
        />
      )}
    </div>
  );
};
